/** @jsx jsx */
import { jsx, Container } from 'theme-ui';
import React, { FunctionComponent } from 'react';
import { graphql, PageProps } from 'gatsby';
import Modal from 'react-modal';
import Layout from '@codersclan/gatsby-theme-airfleet/src/components/layout/layout';
import SectionsLoader from '@codersclan/gatsby-theme-airfleet/src/components/sectionsLoader';

// Types
import { PageDetails as PageDetailsType } from '../../@codersclan/gatsby-theme-airfleet/@types/page';
import { PostDetails } from '../../@codersclan/gatsby-theme-airfleet/@types/post';

// Components
import CustomGrid from '../../components/customGrid';
import Card from '../../components/card';
import Text, { TEXT_TYPES } from '../../components/text';
import CTAButton from '../../components/ctaButton';
import Head from '../../components/head';

// Hooks
import { useWpMetadata } from '../../@codersclan/gatsby-theme-airfleet/hooks/wpMetadata.hook';
import usePagination from '../../hooks/usePagination';
import useSEOPropsResolver from '../../hooks/useSEOPropsResolver';

// Helpers
import { getCategory, getCtaLabel } from '../../helpers/postHelper';
import SearchSectionHelper from '../../helpers/searchSectionHelper';
import merge from 'lodash.merge';

Modal.setAppElement('#___gatsby');

interface Data {
  pagesData: {
    nodes: Array<PageDetailsType>;
  };
  webinarsData: {
    nodes: PostDetails[];
  };
  allWp: {
    nodes: Array<{
      themeGeneralSettings: {
        optionsGeneral: {
          metaTags: {
            value: string;
          };
        };
      };
    }>;
  };
}

type Props = PageProps<Data>;

function getCardContent(webinar: PostDetails) {
  const MAX_W = 172;
  let content = webinar?.seo?.metaDesc || webinar.excerpt;

  if (content.length > MAX_W) {
    content = `${content.slice(0, MAX_W - 3)}...`;
  }

  return content;
}

const PageTemplate: FunctionComponent<Props> = ({ data }) => {
  const {
    titlesSettings: {
      optionsTitles: { webinarSection },
    },
  } = useWpMetadata();
  const { pagesData, webinarsData, allWp } = data;
  const page = pagesData.nodes[0];
  const sections = page?.templateAllPages?.sections || [];

  // States
  const {
    limit: limitUpcoming,
    resetPagination: resetPaginationUpcoming,
    loadMore: loadMoreUpcoming,
  } = usePagination();
  const {
    limit: limitOnDemand,
    resetPagination: resetPaginationOnDemand,
    loadMore: loadMoreOnDemand,
  } = usePagination();

  // Memos
  const upcomingWebinars = React.useMemo(() => {
    if (webinarsData && webinarsData.nodes && webinarsData.nodes.length > 0) {
      const out = webinarsData.nodes.filter(node => {
        const { categories, tags } = node;
        if (
          categories &&
          categories.nodes &&
          categories.nodes.length > 0 &&
          tags &&
          tags.nodes &&
          tags.nodes.length > 0
        ) {
          const isWebinar = categories.nodes.some(n => n.name === 'Webinars');
          const isUpcoming = tags.nodes.some(n => n.name === 'Upcoming');
          return isWebinar && isUpcoming;
        }
        return false;
      });
      return out;
    }
    return [];
  }, [webinarsData]);

  const headerSection = React.useMemo(
    () =>
      SearchSectionHelper.searchSectionHelper(
        sections,
        SearchSectionHelper.SECTIONS.HERO_V2,
      ),
    [sections],
  );

  const prefooterCTASection = React.useMemo(
    () =>
      SearchSectionHelper.searchSectionHelper(
        sections,
        SearchSectionHelper.SECTIONS.PREFOOTER_CTA,
      ),
    [sections],
  );

  const onDemandWebinars = React.useMemo(() => {
    if (webinarsData && webinarsData.nodes && webinarsData.nodes.length > 0) {
      const out = webinarsData.nodes.filter(node => {
        const { categories, tags } = node;
        if (
          categories &&
          categories.nodes &&
          categories.nodes.length > 0 &&
          tags &&
          tags.nodes &&
          tags.nodes.length > 0
        ) {
          const isWebinar = categories.nodes.some(n => n.name === 'Webinars');
          const isUpcoming = tags.nodes.some(n => n.name === 'On-demand');
          return isWebinar && isUpcoming;
        }
        return false;
      });
      return out;
    }
    return [];
  }, [webinarsData]);

  // Effects
  React.useEffect(() => {
    resetPaginationUpcoming();
  }, [upcomingWebinars]);

  React.useEffect(() => {
    resetPaginationOnDemand();
  }, [onDemandWebinars]);

  const [upcomingWebinarsToShow, canShowMoreUpcoming] = React.useMemo(
    () => [
      upcomingWebinars.slice(0, limitUpcoming),
      limitUpcoming < upcomingWebinars.length,
    ],
    [upcomingWebinars, limitUpcoming],
  );

  const [onDemandWebinarsToShow, canShowMoreOnDemand] = React.useMemo(
    () => [
      onDemandWebinars.slice(0, limitOnDemand),
      limitOnDemand < onDemandWebinars.length,
    ],
    [onDemandWebinars, limitOnDemand],
  );

  const { metaTags } = allWp?.nodes[0]?.themeGeneralSettings?.optionsGeneral;
  const seoProps = metaTags
    ? merge({}, useSEOPropsResolver(page), { metaTags: metaTags.value })
    : useSEOPropsResolver(page);

  return (
    <Layout>
      <Head {...seoProps} />
      {/* Top Sections */}
      <SectionsLoader sections={headerSection} />

      {/* Custom Section */}
      <Container
        sx={{
          padding: ['35px 24px 50px', '75px 0px 95px'],
        }}
      >
        {upcomingWebinarsToShow.length > 0 && [
          <Text
            key="upcoming_title"
            type={TEXT_TYPES.H1_BOLD}
            sx={{
              color: 'indigo.100',
              textAlign: 'center',
              marginBottom: ['40px', '75px'],
            }}
          >
            {webinarSection.webinarPageTopTitleTitle}
          </Text>,
          <CustomGrid
            key="upcoming_grid"
            sx={{
              marginBottom: ['40px', '100px'],
            }}
          >
            {upcomingWebinarsToShow.map(webinar => (
              <Card
                key={webinar.id}
                image={webinar.featuredImage?.node}
                category={getCategory(webinar).name}
                categorySlug={getCategory(webinar).slug}
                title={webinar.title}
                content={getCardContent(webinar)}
                cta={{
                  label: getCtaLabel(webinar),
                  url: webinar.uri,
                  target: '_self',
                }}
              />
            ))}
          </CustomGrid>,
        ]}

        {canShowMoreUpcoming && (
          <CTAButton
            key={limitUpcoming}
            type="button"
            alternative
            sx={{ margin: ['40px auto', '60px auto'] }}
            onClick={loadMoreUpcoming}
          >
            View More
          </CTAButton>
        )}

        {onDemandWebinarsToShow.length > 0 && [
          <Text
            key="ondemand_grid"
            type={TEXT_TYPES.H1_BOLD}
            sx={{
              color: 'indigo.100',
              textAlign: 'center',
              marginBottom: ['40px', '75px'],
            }}
          >
            {webinarSection.webinarPageMiddleTitleTitle}
          </Text>,
          <CustomGrid
            sx={{
              marginBottom: ['40px', '65px'],
            }}
            key="ondemand_grid"
          >
            {onDemandWebinarsToShow.map(webinar => (
              <Card
                key={webinar.id}
                image={webinar.featuredImage?.node}
                category={getCategory(webinar).name}
                categorySlug={getCategory(webinar).slug}
                title={webinar.title}
                content={getCardContent(webinar)}
                cta={{
                  label: getCtaLabel(webinar),
                  url: webinar.uri,
                  target: '_self',
                }}
              />
            ))}
          </CustomGrid>,
        ]}

        {canShowMoreOnDemand && (
          <CTAButton
            key={limitOnDemand}
            type="button"
            alternative
            sx={{ margin: ['0px auto 0px', '0px auto 0px'] }}
            onClick={loadMoreOnDemand}
          >
            Load More
          </CTAButton>
        )}
      </Container>

      {/* Bottom Sections */}
      <SectionsLoader sections={prefooterCTASection} />
    </Layout>
  );
};

export default PageTemplate;

export const pageQuery: void = graphql`
  query($slug: String) {
    pagesData: allWpPage(filter: { slug: { eq: $slug } }) {
      nodes {
        ...PageDetailsWithSEOFragment
        templateAllPages {
          sections {
            ...HeroV2Fragment
            ...PreFooterCTAFragment
          }
        }
      }
    }
    allWp {
      nodes {
        themeGeneralSettings {
          optionsGeneral {
            metaTags {
              value
            }
          }
        }
      }
    }
    webinarsData: allWpResource(
      filter: {
        categories: { nodes: { elemMatch: { slug: { eq: "webinars" } } } }
      }
    ) {
      nodes {
        ...BasicResourceFragment
      }
    }
  }
`;
